import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  cancelSubscriptionAction,
  getUserProfileAction,
  getCheckinBusinessBillingSummaryAction,
  getSubscriptionDetailsAction,
  restoreSubscriptionAction,
} from "../../redux/actions";
import { billingCustomerPortalAction } from "../../redux/actions/UserActions/BillingActions";
import ConfirmationMsgModal from "../Modals/ConfirmationMsgModal";
import LocationsModal from "./LocationsModal/LocationsModal";
import MaterialIcons from "../../assests/MaterialIcons/MaterialIcons";
import { menuItemSX } from "../Builder/BuilderConst";
import { useNavigate } from "react-router-dom";
import { routes } from "../../utils/routes";
import "./SubscriptionTab.scss";

interface SubscriptionTabProps {
  data: any;
  dashboardDataLoading: boolean;
}

const formatDate = (date: string | Date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const formatPriceRange = (range: any) => {
  if (!range) return null;

  if (typeof range === "object") {
    const min = range?.min;
    const max = range?.max;
    if (min !== undefined && max !== undefined) {
      if (min === max) {
        return `$${new Intl.NumberFormat().format(min)} per location`;
      }
      return `$${new Intl.NumberFormat().format(min)} - $${new Intl.NumberFormat().format(max)} per location`;
    }
    if (min !== undefined) {
      return `$${new Intl.NumberFormat().format(min)} per location`;
    }
    return null;
  }

  const str = String(range).trim();
  if (!str || str === "[object Object]") return null;
  if (str.includes("location")) {
    return str;
  }
  return `${str} per location`;
};

const SubscriptionTab: React.FC<SubscriptionTabProps> = ({
  data,
  dashboardDataLoading,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const {
    checkinBillingSummary,
    checkinBillingSummaryLoading,
    subscriptionDetails,
    subscriptionDetailsLoading,
  } = useSelector((state: RootState) => state.billing);

  const [openConfimationMsgModal, setOpenConfimationMsgModal] = useState(false);
  const [isLocationsModalOpen, setIsLocationsModalOpen] = useState(false);
  const [locationsModalStatus, setLocationsModalStatus] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleOpenLocationsModal = (status: string = "") => {
    setLocationsModalStatus(status);
    setIsLocationsModalOpen(true);
  };

  useEffect(() => {
    dispatch(getCheckinBusinessBillingSummaryAction());
    dispatch(getSubscriptionDetailsAction());
  }, [dispatch]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCancelPlanData = async () => {
    const payload = {
      id: data?.membershipdata?.subscriptionId,
    };
    await dispatch(cancelSubscriptionAction(payload));
    await dispatch(getUserProfileAction());
    await dispatch(getSubscriptionDetailsAction());
    setOpenConfimationMsgModal(false);
  };

  const handleRestorePlanData = async () => {
    await dispatch(restoreSubscriptionAction());
    await dispatch(getUserProfileAction());
    await dispatch(getSubscriptionDetailsAction());
  };

  const handleCustomerPortalData = async () => {
    if (subData?.portal_url) {
      window.open(subData.portal_url, "_blank", "noopener,noreferrer");
      return;
    }
    dispatch(billingCustomerPortalAction())
      .unwrap()
      .then((res) => {
        const url = res?.data?.url;
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      })
      .catch((err) => console.error(err));
  };

  const handleManagePlanClick = (event: React.MouseEvent<HTMLElement>) => {
    if (subData?.portal_url) {
      window.open(subData.portal_url, "_blank", "noopener,noreferrer");
    } else {
      handleMenuOpen(event);
    }
  };

  // Derive subscription details from API (subscriptionDetails) or fallback to data?.membership
  const subData = subscriptionDetails || data?.membership;

  const planName =
    subData?.plan_name ||
    subData?.planName ||
    subData?.name ||
    subData?.title ||
    "Free";
  const rawInterval =
    subData?.interval || subData?.type || subData?.billing_cycle || "monthly";
  const formattedInterval =
    rawInterval.toLowerCase() === "month" ||
    rawInterval.toLowerCase() === "monthly"
      ? "Monthly"
      : rawInterval.toLowerCase() === "year" ||
          rawInterval.toLowerCase() === "yearly"
        ? "Yearly"
        : rawInterval.charAt(0).toUpperCase() + rawInterval.slice(1);

  const cycleStart =
    subData?.current_period_start ||
    subData?.purchaseDate ||
    subData?.start_date
      ? formatDate(
          subData?.current_period_start ||
            subData?.purchaseDate ||
            subData?.start_date,
        )
      : "";
  const cycleEnd =
    subData?.current_period_end || subData?.expiryDate || subData?.end_date
      ? formatDate(
          subData?.current_period_end ||
            subData?.expiryDate ||
            subData?.end_date,
        )
      : "";
  const nextPaymentDate =
    subData?.next_billing_date ||
    subData?.nextBillingDate ||
    subData?.next_payment_date ||
    subData?.expiryDate
      ? formatDate(
          subData?.next_billing_date ||
            subData?.nextBillingDate ||
            subData?.next_payment_date ||
            subData?.expiryDate,
        )
      : "";

  const planPrice =
    subData?.plan_price ?? subData?.planPrice ?? subData?.price ?? 0;
  const scanCredits =
    subData?.monthly_credits !== undefined && subData?.monthly_credits !== null
      ? new Intl.NumberFormat().format(subData.monthly_credits)
      : "";

  const planId = (
    subData?.plan_id ||
    subData?.planId ||
    subData?.subscription_id ||
    ""
  ).toLowerCase();

  const isFreePlan =
    planId === "free" ||
    planName.toLowerCase() === "free" ||
    subData?.subscription_id === "free" ||
    !subData?.plan_id;

  let paymentMethod = "";
  if (subData?.card_brand && subData?.card_last4) {
    const brand =
      subData.card_brand.charAt(0).toUpperCase() + subData.card_brand.slice(1);
    paymentMethod = `${brand} ending ${subData.card_last4}`;
  } else if (subData?.payment_method || subData?.card_details) {
    paymentMethod = subData.payment_method || subData.card_details;
  }

  const totalLocations = checkinBillingSummary?.total ?? 0;
  const activeLocations = checkinBillingSummary?.active ?? 0;
  const trialingLocations = checkinBillingSummary?.trialing ?? 0;
  const failedLocations = checkinBillingSummary?.failed ?? 0;
  const monthlyTotal = checkinBillingSummary?.monthly_total ?? 0;
  const priceRange = checkinBillingSummary?.price_range;
  const formattedPriceRange = formatPriceRange(priceRange);

  return (
    <Box className="subscription-tab-container">
      {/* ─── Card 1: Map Check-Ins ────────────────────────────────────────── */}
      {checkinBillingSummaryLoading ? (
        <Box className="billing-card" sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box>
              <Skeleton width={180} height={28} />
              <Skeleton width={260} height={18} sx={{ mt: 1 }} />
            </Box>
            <Skeleton width={100} height={36} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Skeleton width={200} height={20} />
            <Skeleton width={110} height={20} />
          </Box>
        </Box>
      ) : (
        <Box className="billing-card">
          <Box className="billing-card-body">
            <Box className="subscription-details-row">
              <Box className="subscription-main-info">
                <Box className="sub-title-wrapper">
                  <h3 className="sub-title">Map Check-Ins</h3>
                  <span className="badge-monthly">Monthly</span>
                </Box>
                <Typography className="cycle-info">
                  {totalLocations}{" "}
                  {totalLocations === 1 ? "location" : "locations"} · each on
                  its own subscription and cycle
                </Typography>
              </Box>

              <Box className="subscription-pricing">
                <Typography className="price-text">
                  ${new Intl.NumberFormat().format(monthlyTotal)}
                  <span className="price-period">/mo</span>
                </Typography>
                {formattedPriceRange && (
                  <Typography className="price-subtext">
                    {formattedPriceRange}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box className="billing-card-status-row">
            <Box className="status-indicators">
              <Box
                className="status-item"
                onClick={() => handleOpenLocationsModal("active")}
              >
                <span className="dot active" />
                <span>{activeLocations} active</span>
              </Box>
              <Box
                className="status-item"
                onClick={() => handleOpenLocationsModal("trialing")}
              >
                <span className="dot trialing" />
                <span>{trialingLocations} trialing</span>
              </Box>
              <Box
                className="status-item"
                onClick={() => handleOpenLocationsModal("failed")}
              >
                <span className="dot failed" />
                <span>{failedLocations} failed</span>
              </Box>
            </Box>

            <span
              className="see-locations-link"
              onClick={() => handleOpenLocationsModal("")}
            >
              See all locations
            </span>
          </Box>
        </Box>
      )}

      {/* ─── Card 2: Subscription / Rank Tracker ────────────────────────── */}
      {dashboardDataLoading || subscriptionDetailsLoading ? (
        <Box className="billing-card" sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box>
              <Skeleton width={180} height={28} />
              <Skeleton width={240} height={18} sx={{ mt: 1 }} />
              <Skeleton width={220} height={18} sx={{ mt: 0.5 }} />
            </Box>
            <Skeleton width={100} height={36} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Skeleton width={260} height={20} />
            <Skeleton width={100} height={20} />
          </Box>
        </Box>
      ) : (
        <Box className="billing-card">
          <Box className="billing-card-body">
            <Box className="subscription-details-row">
              <Box className="subscription-main-info">
                <Box className="sub-title-wrapper">
                  <h3 className="sub-title">Rank Tracker</h3>
                </Box>
                <Typography className="plan-title-light">
                  {planName} <span className="badge-pro">{formattedInterval}</span>
                </Typography>
                {isFreePlan ? (
                  <Typography className="cycle-info">
                    Upgrade plan to unlock all features
                  </Typography>
                ) : (
                  <Typography className="cycle-info">
                    Current cycle: {cycleStart} – {cycleEnd}
                  </Typography>
                )}
                <Typography className="renew-info">
                  {subData?.cancel_at_period_end
                    ? `Plan will cancel at ${
                        subData?.cancel_at
                          ? formatDate(subData.cancel_at)
                          : cycleEnd || "month end"
                      }`
                    : "One subscription for the whole workspace"}
                </Typography>
              </Box>

              <Box className="subscription-pricing">
                <Typography className="price-text">
                  ${planPrice}
                  <span className="price-period">/mo</span>
                </Typography>
                <Typography className="price-subtext">
                  {scanCredits} scan credits / mo
                </Typography>
                <Box className="action-button-wrapper">
                  {isFreePlan ? (
                    <Button
                      onClick={() =>
                        navigate(routes?.upgrade || "/upgrade", {
                          state: { tab: 1 },
                        })
                      }
                      variant="outlined"
                      className="btn-pill"
                    >
                      Upgrade plan
                    </Button>
                  ) : (subData?.status === "canceled" ||
                      subData?.subscription_status === "canceled") &&
                    !subData?.cancel_at_period_end ? (
                    <Button
                      onClick={() =>
                        navigate(routes?.upgrade || "/upgrade", {
                          state: { tab: 1 },
                        })
                      }
                      variant="outlined"
                      className="btn-pill"
                    >
                      Re-subscribe
                    </Button>
                  ) : subData?.cancel_at_period_end ? (
                    <Button
                      onClick={handleRestorePlanData}
                      variant="outlined"
                      className="btn-pill"
                    >
                      Restore plan
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setOpenConfimationMsgModal(true)}
                      variant="outlined"
                      className="btn-pill"
                    >
                      Cancel plan
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {!isFreePlan && (
            <Box className="billing-card-footer">
              <span className="footer-left-text">
                Next payment {nextPaymentDate} · {paymentMethod}
              </span>
              <span
                className="footer-right-link"
                onClick={handleManagePlanClick}
              >
                Manage plan
              </span>
            </Box>
          )}
        </Box>
      )}

      {/* ─── Manage Plan Dropdown Menu ───────────────────────────────────── */}
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        MenuListProps={{ dense: true }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "12px",
            minWidth: 160,
            border: "1px solid #e5e7eb",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
            padding: "2px 0",
          },
        }}
      >
        <MenuItem
          sx={menuItemSX}
          onClick={() => {
            handleMenuClose();
            navigate(routes?.upgrade);
          }}
        >
          <MaterialIcons.TrendingChart
            style={{ width: "18px", marginRight: "10px" }}
          />
          Upgrade plan
        </MenuItem>

        <MenuItem
          sx={menuItemSX}
          onClick={() => {
            handleMenuClose();
            handleCustomerPortalData();
          }}
        >
          <MaterialIcons.CreditCard
            style={{ width: "18px", marginRight: "10px" }}
          />
          Payment Method
        </MenuItem>

        <Divider />

        <MenuItem
          sx={{
            py: "6px !important",
            px: "12px !important",
            mx: "4px !important",
            my: "2px !important",
            borderRadius: "var(--small-border-radius) !important",
            color:
              subData?.status === "canceled" ||
              subData?.subscription_status === "canceled" ||
              subData?.cancel_at_period_end
                ? "#1A73E8 !important"
                : "red !important",
            fontWeight: "400 !important",
            fontSize: "14px !important",
            fontFamily: "var(--font-family-manrope)",
            "&:hover": {
              backgroundColor:
                subData?.status === "canceled" ||
                subData?.subscription_status === "canceled" ||
                subData?.cancel_at_period_end
                  ? "#e8f0fe !important"
                  : "#fceced !important",
            },
          }}
          onClick={() => {
            handleMenuClose();
            if (
              (subData?.status === "canceled" ||
                subData?.subscription_status === "canceled") &&
              !subData?.cancel_at_period_end
            ) {
              navigate(routes?.upgrade, { state: { tab: 1 } });
            } else if (subData?.cancel_at_period_end) {
              handleRestorePlanData();
            } else {
              setOpenConfimationMsgModal(true);
            }
          }}
        >
          <MaterialIcons.Close style={{ width: "18px", marginRight: "10px" }} />
          {(subData?.status === "canceled" ||
            subData?.subscription_status === "canceled") &&
          !subData?.cancel_at_period_end
            ? "Re-subscribe"
            : subData?.cancel_at_period_end
              ? "Restore plan"
              : "Cancel plan"}
        </MenuItem>
      </Menu>

      {/* ─── Confirmation Modal ─────────────────────────────────────────── */}
      <ConfirmationMsgModal
        open={openConfimationMsgModal}
        handleClose={() => setOpenConfimationMsgModal(false)}
        onConfirm={handleCancelPlanData}
        title="Cancel Plan"
        isCancel={true}
        msg="Are you sure want to cancel this plan?"
      />

      {/* ─── Locations Table Modal ──────────────────────────────────────── */}
      <LocationsModal
        open={isLocationsModalOpen}
        handleClose={() => setIsLocationsModalOpen(false)}
        initialStatusFilter={locationsModalStatus}
      />
    </Box>
  );
};

export default SubscriptionTab;
